<!--
 * @Description : 发送手机验证码3.0（公用）
 * @Author      : pishengjun
 * @Date        : 2025-03-12 14:38:37
-->

<template>
  <div class="sendSmsBoxWrap">
    <div class="sendSmsBoxText">
      <a-button v-if="!isSend" type="link" size="small" class="text-blue-600" :loading="loading" @click.Stop="clickSend()">获取验证码</a-button>
      <span v-else class="tipInfo">
        <a-statistic-countdown class="countdown" :value="showTime" format="s秒后重试" @finish="onCountdownFinish()" />
      </span>
    </div>
    <!-- 发送验证码时的验证码 -->
    <div v-if="open" class="sendSmsBox">
      <div class="arrow"></div>
      <div class="RandomVerificationCodeWrap">
        <RandomVerificationCode v-if="open" ref="RandomVerificationCodeRef" class="RandomVerificationCode" @onGetData="onGetRandomVerificationCodeMd5"></RandomVerificationCode>
      </div>
      <div class="RandomVerificationCode_input">
        <a-input ref="verificationCodeInputRef" auto class="verificationCodeInput" placeholder="请输入图中6位验证码" :value="value" :maxlength="6" @input="onChangeCode" />
        <div v-show="errMsg" class="errMsg">{{ errMsg }}</div>
      </div>
    </div>
  </div>
  <Teleport to="#app">
    <div v-if="open" class="sendPhonecodeMark" @click="open = false"></div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, nextTick } from "vue";
import { message } from "ant-design-vue";
import { md5 } from "js-md5";
import RandomVerificationCode from "@/components/randomVerificationCode/index.vue";
import type { RandomVerificationCodeMd5 } from "@/http/interfaceModel/FaceRecognitionService";
import { RegisteredUserExamerClass } from "@/http/api/FaceRecognitionService";
import { Base64 } from "js-base64";
import { debounce } from "lodash-es";

export default defineComponent({
  props: {
    // 图形验证码value
    value: {
      type: String,
      require: true,
      default: "",
    },
    // 倒计时时间,默认600秒
    time: {
      type: Number,
      default: 600,
    },
    phone: {
      type: String,
      require: true,
      default: "",
    },
    // 手机短信验证码类型：0-注册 1-登录 2-找回密码 3-更换手机号码 4-流程环节通知  5-报名完成  6以上-自定义  （查询地址：管理端-系统管理-短信消息配置-短信模板类型）
    type: {
      type: Number,
      require: true,
      default: 0,
    },
    code: {
      type: String,
      require: true,
      default: "",
    },
  },
  components: { RandomVerificationCode },
  emits: ["onFinish", "update:value"],
  setup(props, { emit }) {
    const state = reactive({
      showTime: Date.now() + props.time * 1000,
      open: false,
      isSend: false,
      loading: false,
      errMsg: "",
      verificationCodeInputRef: null as any,
      randomVerificationCodeMd5Data: {} as RandomVerificationCodeMd5,
      RandomVerificationCodeRef: null as InstanceType<typeof RandomVerificationCode> | null,
    });

    const event = {
      onGetRandomVerificationCodeMd5(data: RandomVerificationCodeMd5) {
        state.randomVerificationCodeMd5Data = data;
        state.errMsg = "";
        nextTick(() => {
          // input 输入6位图形验证码输入框自动获取焦点
          if (!!state.verificationCodeInputRef) {
            state.verificationCodeInputRef.focus();
          }
        });
        emit("update:value", "");
      },
      onInput(e: any) {
        const value = e.target.value;
        emit("update:value", value);
      },
      clickSend() {
        state.open = !state.open;
      },
      // 获取验证码
      getRandomVerificationCode() {
        state.RandomVerificationCodeRef?.GetRandomVerificationCodeMd5();
      },
      // 加密多次
      encryptMultipleTimes(value: string, times: number) {
        let hash = value;
        for (let i = 0; i < times; i++) {
          hash = md5(hash).toUpperCase();
        }
        return hash;
      },

      async onChangeCode(e: any) {
        const value = e.target.value;
        // 使用防抖函数处理验证码验证
        debouncedVerifyCode(value);
      },
      onCountdownStart() {
        state.isSend = true;
        state.showTime = Date.now() + props.time * 1000;
      },
      onCountdownFinish() {
        console.log("onCountdownFinish");
        state.isSend = false;
        state.showTime = Date.now() + props.time * 1000;
      },
    };
    // 创建防抖版本的验证码验证函数
    const debouncedVerifyCode = debounce(async (value: string) => {
      state.errMsg = "";
      emit("update:value", value);
      if (value.length < 6) return;
      if (md5(value).toUpperCase() != state.randomVerificationCodeMd5Data.verificationCodeMd5) {
        state.errMsg = "请输入正确的图形验证码！";
        return;
      }
      state.open = false;
      // 判断手机号是否为空,如果有code则可不需要手机号
      if (!props.code && !props.phone) return message.error("手机号不能为空！");
      await RegisteredUserExamerClass.SendPhoneCode({
        phone: Base64.encode(props.phone),
        messageType: props.type,
        randomMark: Base64.encode(state.randomVerificationCodeMd5Data.randomMark),
        randomVerificationCode: Base64.encode(value),
        code: props.code,
      });
      event.onCountdownStart();
      message.success("验证码已发送,请注意查收");
      emit("onFinish", {
        randomMark: state.randomVerificationCodeMd5Data.randomMark,
        verificationCodeImageBase64: state.randomVerificationCodeMd5Data.verificationCodeImageBase64,
        verificationCodeMd5: state.randomVerificationCodeMd5Data.verificationCodeMd5,
        randomVerificationCode: value,
      });
    }, 50); // 500ms 防抖延迟
    return {
      ...toRefs(state),
      ...event,
    };
  },
});
</script>

<style lang="less" scoped>
.sendSmsBoxWrap {
  display: inline-flex;
  justify-content: flex-end;
  position: relative;
  z-index: 100;

  .sendSmsBoxText {
    .tipInfo {
      color: #bfbfbf;
      :deep(.countdown) {
        .ant-statistic-content {
          font-size: 14px;
          color: #bfbfbf;
        }
      }
    }
  }

  .sendSmsBox {
    position: absolute;
    z-index: 101;
    right: 3px;
    width: 200px;
    top: 100%;
    box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
    background: #fff;
    padding: 12px;
    line-height: 1.6;
    .title {
      color: #333;
      font-size: 14px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .t {
        font-weight: bold;
      }
      .ico {
        cursor: pointer;
      }
    }
    .RandomVerificationCodeWrap {
      border: 1px solid #ccc;
      margin: 10px 0;
      .RandomVerificationCode {
        width: 100%;
        height: auto;
        min-width: 166px;
        min-height: 43px;
      }
    }
    .verificationCodeImg {
      width: 100%;
      margin: 0;
    }
    .verificationCodeInput {
      width: 100%;
      // border-radius: 50px;
      overflow: hidden;
      // border: 2px solid #0099ff;
      // background-image: radial-gradient(#ffffff 0%, #ffffff 50%, #eaf6ff 100%);
      text-align: left;
      font-size: 18px;
      font-weight: 700;
      height: 34px;
      overflow: hidden;
      padding: 4px 11px;
      color: #000;
      letter-spacing: 10px;
      &::placeholder {
        // color: #0099ff;
        // text-align: center;
        font-size: 14px;
        font-weight: 400;
        letter-spacing: 0px;
      }
    }
    .arrow {
      display: block;
      position: absolute;
      background-image: url(@/assets/images/tips.gif);
      background-repeat: no-repeat;
      width: 16px;
      height: 8px;
      background-position: 0 0;
      overflow: hidden;
      top: -8px;
      right: 40px;
    }
    .errMsg {
      color: red;
      padding: 3px 10px 0;
      font-size: 12px;
      line-height: 16px;
      text-align: center;
    }
  }
}

.sendPhonecodeMark {
  position: fixed;
  z-index: 0;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}
</style>
